import { useState } from "react";
import { AlertTriangle, Sparkles, X } from "lucide-react";

/**
 * V1-A — Pre-submit cost confirmation modal.
 *
 * Built now even though V1-A only ships manual_import (cost = $0).
 * V1-B's Sora 2 path drops paid providers in WITHOUT any UI work
 * because this modal is already wired into the submit flow.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onConfirm: () => void   // parent runs the submit on confirm
 *   provider: string        // e.g. "manual_import" | "sora-2"
 *   costUsd: number         // 0 for manual_import; >0 for paid providers
 *   currency: string        // "USD"
 *   durationSeconds: number
 *   size: string            // "1280x720"
 *   shotId: string
 *   busy: boolean
 */
export default function VideoCostModal({
    open,
    onClose,
    onConfirm,
    provider,
    costUsd = 0,
    currency = "USD",
    durationSeconds,
    size,
    shotId,
    busy = false,
}) {
    const [ack, setAck] = useState(false);
    if (!open) return null;
    const isFree = !costUsd || costUsd <= 0;

    return (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4"
            data-testid="video-cost-modal"
        >
            <div className="w-full max-w-md bg-[#0a0a0a] border border-zinc-800 rounded-md p-6 ceo-glow">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                        {isFree
                            ? <Sparkles className="w-5 h-5 text-emerald-300" />
                            : <AlertTriangle className="w-5 h-5 text-amber-300" />}
                        <h3 className="font-display text-lg">
                            {isFree ? "Confirm import" : "Confirm video generation"}
                        </h3>
                    </div>
                    <button
                        data-testid="video-cost-close"
                        onClick={onClose}
                        className="text-zinc-500 hover:text-zinc-200"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="space-y-2 text-sm text-zinc-300 mb-5">
                    <Row label="Shot" value={shotId} />
                    <Row label="Provider" value={provider} />
                    <Row label="Duration" value={`${durationSeconds || "—"}s`} />
                    <Row label="Size" value={size || "—"} />
                    <Row
                        label="Estimated cost"
                        value={isFree
                            ? "Free (manual upload)"
                            : `~${currency} ${Number(costUsd).toFixed(2)}`}
                        tone={isFree ? "good" : "warn"}
                    />
                </div>

                {!isFree && (
                    <label className="flex items-start gap-2 mb-4 text-xs text-zinc-400">
                        <input
                            type="checkbox"
                            data-testid="video-cost-ack"
                            checked={ack}
                            onChange={(e) => setAck(e.target.checked)}
                            className="mt-0.5"
                        />
                        <span>
                            I understand this will use Universal Key / provider credits. There are no refunds for completed jobs.
                        </span>
                    </label>
                )}

                <div className="flex items-center justify-end gap-2">
                    <button
                        data-testid="video-cost-cancel"
                        onClick={onClose}
                        className="text-sm text-zinc-400 hover:text-zinc-200 px-3 py-1.5"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="video-cost-confirm"
                        disabled={busy || (!isFree && !ack)}
                        onClick={onConfirm}
                        className="btn-primary text-sm disabled:opacity-50"
                    >
                        {busy ? "Submitting…" : isFree ? "Attach to shot" : "Generate"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, tone }) {
    const cls = tone === "warn"
        ? "text-amber-300"
        : tone === "good"
            ? "text-emerald-300"
            : "text-zinc-100";
    return (
        <div className="flex items-center justify-between border-b border-zinc-900 pb-1.5 last:border-b-0">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
            <span className={`text-sm ${cls}`}>{value}</span>
        </div>
    );
}
